import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  AdjustStockRequest,
  CreateStockTransferRequest,
  ExpiringBatchDto,
  ItemStockDto,
  StockMovementDto,
  StockTransferDto,
} from './types';

export const QK = {
  stocks: (branchId: string | null) => ['inventory', 'stocks', branchId] as const,
  movements: (branchId: string | null, itemId: string | null) =>
    ['inventory', 'movements', branchId, itemId] as const,
};

export function useStocks(branchId: string | null) {
  return useQuery({
    enabled: !!branchId,
    queryKey: QK.stocks(branchId),
    queryFn: async () =>
      (await apiClient.get<ItemStockDto[]>('/api/inventory/stocks', {
        params: { branchId },
      })).data,
    staleTime: 10_000,
  });
}

export function useMovements(branchId: string | null, itemId: string | null) {
  return useQuery({
    enabled: !!branchId && !!itemId,
    queryKey: QK.movements(branchId, itemId),
    queryFn: async () =>
      (await apiClient.get<StockMovementDto[]>('/api/inventory/movements', {
        params: { branchId, itemId, limit: 100 },
      })).data,
    staleTime: 10_000,
  });
}

export function useAdjustStock(branchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: AdjustStockRequest) =>
      (await apiClient.post('/api/inventory/adjustments', req)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.stocks(branchId) });
      // Movements queries are keyed per-item; invalidate the whole tree.
      qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
    },
  });
}

// ---- Stock transfers (Phase 2C) ----

export function useTransfers(branchId: string | null) {
  return useQuery({
    queryKey: ['inventory', 'transfers', branchId],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (branchId) params['branchId'] = branchId;
      return (await apiClient.get<StockTransferDto[]>('/api/inventory/transfers', { params })).data;
    },
    staleTime: 10_000,
  });
}

export function useCreateTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateStockTransferRequest) =>
      (await apiClient.post<StockTransferDto>('/api/inventory/transfers', req)).data,
    onSuccess: () => {
      // Transfer affects stock at both branches + movement log.
      qc.invalidateQueries({ queryKey: ['inventory', 'transfers'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'stocks'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
    },
  });
}

// ---- Phase 4F-1 expiring-soon report ----

export function useExpiring(branchId: string | null, withinDays: number) {
  return useQuery({
    enabled: !!branchId,
    queryKey: ['inventory', 'expiring', branchId, withinDays] as const,
    queryFn: async () =>
      (
        await apiClient.get<ExpiringBatchDto[]>('/api/inventory/expiring', {
          params: { branchId, withinDays },
        })
      ).data,
    staleTime: 30_000,
  });
}
